/*
  assets/mad-estimate-list.js: the estimate list (plan section 5.4, item 4).

  Doors priced with services live here, in the browser, never in the payable Shopify cart, so
  the cart only ever holds door-only items that check out normally. The list is sent for a
  reviewed quote through the same webhook as before: toCart() is a versioned adapter that writes
  the list in the /cart.js shape the Zap already parses (cart_json: items with variant_id,
  quantity, product_title, final_line_price and properties including _raw).

  What is stored: product and configuration data only (ids, title, handle, url, image, price,
  tags, type, options, the _raw and its display properties). No names, emails, phones or
  addresses. Retention: 30 days after the last change (FOR APPROVAL), then the list clears
  itself. When the browser refuses storage the list lives in memory and persistent() is false,
  so the page warns that it will not survive closing the tab.

  Storage denied (Codex final review R03, 2026-09-26): localStorage first; when the browser refuses
  it, sessionStorage, which keeps the list across the pages of this tab (Review estimate opens the
  cart in the same tab) but not after the tab closes; when both are refused, memory only, which a
  page change loses. storageMode() says which ('local', 'session' or 'memory') and limitText() is the
  one sentence the pages show about it. Codex re-check (2026-09-26): a write the browser refuses later
  (storage full, or blocked after the page loaded) moves the list to sessionStorage too, before memory,
  and the next page of the tab reads that copy while it is the newer one. In memory mode the limit is
  said on the added screens before anything is lost, and Review estimate opens the review and the quote
  request in place, on the same page (openReview: the cart page's estimate block and shared step, from
  its sections), so nothing is lost by leaving; leaving any other way gets the browser's own "leave this
  page?" question, until the estimate is sent or cleared.

  Alternatives ("Alternative to", 2026-09-25) were removed on 2026-10-12 (Seth: "it just over complicates
  things for no reason"): every door on the list counts; a group id saved before then is ignored.

  Two tabs: a change in one tab reaches the other through the storage event.
  One switch: ENABLED = false puts customized doors back into the Shopify cart (the 2026-09-18
  behaviour); every caller checks MadEstimateList.enabled.
*/
(function () {
  'use strict';
  if (window.MadEstimateList) return;

  var ENABLED = true;
  var KEY = 'mad_estimate_v1';
  var VERSION = 1;
  var ADAPTER = 'mad-estimate-list/1';
  var RETENTION_DAYS = 30; /* FOR APPROVAL: how long an unsent or sent estimate is kept in the browser */
  var DAY = 86400000;

  function usable(name) {
    try { var s = window[name]; s.setItem('__mad_probe', '1'); s.removeItem('__mad_probe'); return s; } catch (e) { return null; }
  }
  function stored(s) {
    try { var d = JSON.parse(s.getItem(KEY) || 'null'); return d && d.v === VERSION && Array.isArray(d.lines) ? d : null; } catch (e) { return null; }
  }
  var store = null, mem = null, mode = 'memory';
  var local = usable('localStorage'), session = usable('sessionStorage');
  if (local) { store = local; mode = 'local'; }
  else if (session) { store = session; mode = 'session'; }
  /* A list a later write could not keep in localStorage went on in sessionStorage (write below): this tab reads that copy
     while it is the newer one (a full localStorage still passes the small probe above). */
  if (local && session) {
    var inSession = stored(session), inLocal = inSession ? stored(local) : null;
    if (inSession && (!inLocal || (Number(inSession.updatedAt) || 0) >= (Number(inLocal.updatedAt) || 0))) { store = session; mode = 'session'; }
  }
  var persistent = mode === 'local';

  function now() { return Date.now(); }
  function blank() { return { v: VERSION, updatedAt: now(), sentAt: null, leadId: null, lines: [] }; }

  function read() {
    if (!store) return mem || (mem = blank());
    var d = null;
    try { d = JSON.parse(store.getItem(KEY) || 'null'); } catch (e) { d = null; }
    if (!d || d.v !== VERSION || !Array.isArray(d.lines)) return blank();
    if (now() - (Number(d.updatedAt) || 0) > RETENTION_DAYS * DAY) {
      try { store.removeItem(KEY); } catch (e) { /* ignore */ }
      return blank();
    }
    return d;
  }

  function write(d) {
    d.updatedAt = now();
    if (store) {
      var text = JSON.stringify(d);
      try { store.setItem(KEY, text); return; } catch (e) { /* refused: sessionStorage, then memory */ }
      /* Codex re-check (R03): a write the browser refuses after the page loaded (storage full, or blocked since) keeps the
         list in sessionStorage, which the next page of this tab reads, before memory, which a page change loses. The
         older localStorage copy is dropped once the session copy holds, so it never stands in for this one. */
      if (mode === 'local') {
        var s2 = usable('sessionStorage');
        if (s2) {
          try {
            s2.setItem(KEY, text);
            try { store.removeItem(KEY); } catch (e3) { /* the newer session copy is read first anyway */ }
            store = s2; mode = 'session'; persistent = false;
            return;
          } catch (e2) { /* memory */ }
        }
      }
      store = null; persistent = false; mode = 'memory';
    }
    mem = d;
  }

  function emit(reason, extra) {
    var detail = Object.assign({ reason: reason, lines: lines(), persistent: persistent }, extra || {});
    try { document.dispatchEvent(new CustomEvent('mad:estimate:changed', { detail: detail })); } catch (e) { /* old browsers */ }
  }

  function rnd() {
    try { var a = new Uint32Array(1); window.crypto.getRandomValues(a); return a[0].toString(36); } catch (e) { return Math.random().toString(36).slice(2, 9); }
  }

  /* The product fields the estimate needs, nothing else. name is the door's short display name (from
     snippets/mad-card-name.liquid through the page's product object; '' on an object without one): the list shows
     it, and toCart() never sends it, so the Zap still reads the real title (polish, 2026-09-26). */
  function slim(p) {
    p = p || {};
    var out = {
      id: Number(p.id) || null, variantId: Number(p.variantId) || null, title: String(p.title || ''), name: String(p.name || ''), handle: String(p.handle || ''),
      url: String(p.url || ''), image: String(p.image || ''), price: Number(p.price) || 0, available: p.available !== false,
      tags: Array.isArray(p.tags) ? p.tags.slice() : [], productType: String(p.productType || ''), vendor: String(p.vendor || ''),
      sku: String(p.sku || ''), options: Array.isArray(p.options) ? p.options : []
    };
    /* R04: the size the product page showed (snippets/mad-size-json.liquid), so an Edit or a summary on the cart page
       measures against the same size; toCart() never sends it */
    var z = p.size;
    if (z && typeof z === 'object') out.size = { width: String(z.width || ''), height: String(z.height || ''), thickness: String(z.thickness || ''), source: String(z.source || ''), check: String(z.check || ''), text: String(z.text || '') };
    return out;
  }

  /* Codex re-check (R04 and R10): an entry saved before the trusted-size fix and the short name is normalized as it is
     read, for the screen, the maths and the quote alike (every caller reads through lines() or get(); toCart() too):
     advice computed from a size the page no longer trusts is recomputed or sent to review, and its display properties
     and _raw follow (MADEstimate.normalizeSavedLine; the customer's own choices stay); a door without its short name gets
     the one the page gives it (MADEstimate.shortName). Nothing is written back; an entry saved since is returned as is.
     Needs the maths and the price book, so it applies on the pages that show, price or send the list. */
  function normalized(l) {
    var E = window.MADEstimate, book = window.MAD_PRICE_BOOK;
    if (!l || !E || !book || !E.normalizeSavedLine) return l;
    var out = l, p = l.product || {};
    if (!p.name && E.shortName && E.doorConfig(p.tags)) out = Object.assign({}, out, { product: Object.assign({}, p, { name: E.shortName(p) }) });
    var n = E.normalizeSavedLine(l.raw, l.properties, out.product, book);
    if (n.changed) out = Object.assign({}, out, { raw: n.raw, properties: n.properties });
    return out;
  }
  function lines() { return read().lines.map(normalized); }
  function get(id) { return normalized(read().lines.filter(function (l) { return l.id === id; })[0] || null); }
  /* The first entry for this variant, or null (Release B: an antique is one of one, so the form opens and updates the
     entry it has instead of adding a second copy) */
  function findByVariant(variantId) {
    var v = Number(variantId);
    if (!v) return null;
    return normalized(read().lines.filter(function (l) { return l.product && Number(l.product.variantId) === v; })[0] || null);
  }
  function count() { return read().lines.length; }
  function meta() { var d = read(); return { sentAt: d.sentAt, leadId: d.leadId, updatedAt: d.updatedAt, sentSig: d.sentSig || null, sentTimeline: d.sentTimeline || null }; }

  /* properties: the display properties plus _raw, exactly what /cart/add.js would have received. Each line keeps
     its own copy of the choices, so a change to one door never reaches another (also in the memory fallback). */
  function copy(o) { return o == null ? o : JSON.parse(JSON.stringify(o)); }
  function entry(product, raw, properties) {
    return { id: 'e' + now().toString(36) + rnd(), addedAt: now(), updatedAt: now(), product: slim(product), raw: copy(raw), properties: copy(properties) || { _raw: JSON.stringify(raw) } };
  }
  /* Several doors in one write (Codex final review R01, 2026-09-26): the units of one cart line join the list
     together or not at all. Each is its own door, sent to the Zap as its own quantity 1 line by toCart(), so every
     unit and its services are priced, and each opening can get its own choices later. items: [{ product, raw,
     properties }]. Returns the new lines. */
  function addAll(items) {
    var added = (items || []).map(function (x) { return entry(x.product, x.raw, x.properties); });
    if (!added.length) return [];
    var d = read();
    d.lines = d.lines.concat(added);
    d.sentAt = null;
    write(d);
    emit('add', { line: added[0], added: added });
    return added;
  }
  function add(product, raw, properties) { return addAll([{ product: product, raw: raw, properties: properties }])[0]; }

  function update(id, product, raw, properties) {
    var d = read(), line = null;
    d.lines.forEach(function (l) {
      if (l.id !== id) return;
      l.product = slim(product || l.product); l.raw = raw; l.properties = properties || { _raw: JSON.stringify(raw) }; l.updatedAt = now();
      line = l;
    });
    if (!line) return null;
    d.sentAt = null;
    write(d);
    emit('update', { line: line });
    return line;
  }

  function remove(id) {
    var d = read(), before = d.lines.length;
    d.lines = d.lines.filter(function (l) { return l.id !== id; });
    if (d.lines.length === before) return false;
    write(d);
    emit('remove', { id: id });
    return true;
  }

  function clear() { write(blank()); emit('clear'); }

  /* extra (QA Q-04, after a reload): sig is a short hash of the estimate that was sent (doors, choices, prices,
     total; no contact fields) and timeline its lead-time choice, so a reload can still tell an unchanged
     estimate (it reopens on its confirmation) from a changed one (the form, Send ready). */
  function markSent(leadId, extra) {
    var d = read(); d.sentAt = now(); d.leadId = leadId || null;
    d.sentSig = (extra && extra.sig) || null; d.sentTimeline = (extra && extra.timeline) || null;
    write(d); emit('sent');
  }

  /* ── the adapter: the list in the /cart.js shape the Zap reads (cart_json) ── */
  function toCart(list) {
    var all = list || lines();
    var items = all.map(function (l) {
      var p = l.product || {}, price = Number(p.price) || 0;
      var props = Object.assign({}, l.properties || { _raw: JSON.stringify(l.raw || {}) });
      /* an "Alternatives" property saved by the removed control (before 2026-10-12) is not sent */
      delete props['Alternatives'];
      return {
        id: p.variantId, key: l.id, variant_id: p.variantId, product_id: p.id, product_title: p.title, title: p.title,
        handle: p.handle, url: p.url, image: p.image, sku: p.sku, vendor: p.vendor, product_type: p.productType,
        quantity: 1, price: price, original_price: price, final_price: price, line_price: price, original_line_price: price, final_line_price: price,
        properties: props
      };
    });
    var total = items.reduce(function (s, i) { return s + i.final_line_price; }, 0);
    return { token: '', note: null, attributes: { estimate_list: ADAPTER }, currency: 'USD', adapter: ADAPTER,
      item_count: items.length, total_price: total, original_total_price: total, items_subtotal_price: total, requires_shipping: true, items: items };
  }

  /* One product's live state from /products/<handle>.js:
       ok       the product JSON (then the variant decides)
       gone     404 or 410 (archived or deleted), or a response that is not product JSON, which is
                what a redirected handle returns (D11 archives or redirects a sold antique right away)
       unknown  a network error, 5xx, 429 or any other refusal: we cannot tell, keep what we have */
  function probe(f, handle) {
    var UNKNOWN = { state: 'unknown' }, GONE = { state: 'gone' };
    return Promise.resolve().then(function () {
      return f('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    }).then(function (r) {
      if (!r) return UNKNOWN;
      var status = Number(r.status) || 0;
      if (status === 404 || status === 410) return GONE;
      if (!r.ok) return UNKNOWN;
      var type = '';
      try { type = String((r.headers && r.headers.get && r.headers.get('content-type')) || '').toLowerCase(); } catch (e) { type = ''; }
      return Promise.resolve().then(function () { return r.json(); }).then(function (pj) {
        return pj && Array.isArray(pj.variants) ? { state: 'ok', product: pj } : GONE;
      }, function () {
        /* a body that says it is JSON but did not parse is a transfer problem, not a sold door */
        return type.indexOf('json') !== -1 && !r.redirected ? UNKNOWN : GONE;
      });
    }, function () { return UNKNOWN; });
  }

  function money(cents) {
    var E = window.MADEstimate, n = (Number(cents) || 0) / 100;
    return E && E.money ? E.money(n) : '$' + n.toFixed(2);
  }

  /* A changed door price also changes this line's stamped estimate (services are unchanged), so the
     _raw estimate_total and the "Estimate before freight" property sent to the Zap follow it. */
  function repriceLine(l, from, to) {
    l.product.price = to;
    if (!l.raw || l.raw.estimate_total === undefined) return;
    var total = Math.round(Number(l.raw.estimate_total) * 100) + (to - from);
    l.raw.estimate_total = (total / 100).toFixed(2);
    var props = l.properties || {};
    if (props['Estimate before freight'] !== undefined) props['Estimate before freight'] = money(total);
    props._raw = JSON.stringify(l.raw);
    l.properties = props;
  }

  /* Live price and availability before sending (the Zap prices the door from the variant anyway).
     A door that is archived, redirected, deleted, or whose variant is gone or unavailable is sold.
     Availability is always written back, so a door that comes back (a cancelled sale) is available
     again. Nothing is written or emitted when nothing changed, so a resend keeps its lead id. */
  function refresh(fetchImpl) {
    var f = fetchImpl || window.fetch;
    var handles = {};
    lines().forEach(function (l) { if (l.product && l.product.handle) handles[l.product.handle] = true; });
    var names = Object.keys(handles);
    return Promise.all(names.map(function (h) {
      return probe(f, h).then(function (res) { return [h, res]; });
    })).then(function (pairs) {
      var byHandle = {}; pairs.forEach(function (x) { byHandle[x[0]] = x[1]; });
      var d = read(), sold = [], changed = [], restored = [], dirty = false;
      d.lines.forEach(function (l) {
        var res = l.product && byHandle[l.product.handle];
        if (!res || res.state === 'unknown') return; /* unknown: keep what we have; the Zap checks the variant */
        var v = res.state === 'ok' ? (res.product.variants || []).filter(function (x) { return Number(x.id) === Number(l.product.variantId); })[0] : null;
        var avail = !!v && v.available !== false;
        if (!avail) sold.push(l);
        if (avail && l.product.available === false) restored.push(l);
        if (l.product.available !== avail) { l.product.available = avail; dirty = true; }
        if (v && typeof v.price === 'number' && v.price !== l.product.price) {
          changed.push({ id: l.id, title: l.product.title, from: l.product.price, to: v.price });
          repriceLine(l, l.product.price, v.price);
          dirty = true;
        }
      });
      if (dirty) { write(d); emit('refresh', { sold: sold, changed: changed, restored: restored }); }
      return { sold: sold, changed: changed, restored: restored };
    });
  }

  /* ── storage denied (R03): say it before anything is lost ─────────── */
  function phone() { return (window.MadQuoteConfig && window.MadQuoteConfig.phone) || '(337) 273-0189'; }
  /* where: 'cart' on the cart page (the quote is requested there), anything else on the added screens */
  function limitText(where) {
    /* the form's added screen: no call line inside the form (Seth 2026-10-11, one call at the end of the request) */
    if (where === 'form') {
      if (mode === 'session') return 'This browser keeps your estimate only while this tab is open. Request your quote before you close it.';
      if (mode === 'memory') {
        var onCart = false;
        try { onCart = location.pathname === ((window.MadQuoteConfig && window.MadQuoteConfig.cartUrl) || '/cart'); } catch (_) { onCart = false; }
        return 'This browser is not saving your estimate, so it is lost when you leave this page. '
          + (onCart ? 'Request your quote before you leave.' : 'Review estimate opens it right here to request your quote.');
      }
      return '';
    }
    if (mode === 'session') return 'This browser keeps your estimate only while this tab is open. Request your quote before you close it, or call ' + phone() + '.';
    if (mode === 'memory') return 'This browser is not saving your estimate, so it is lost when you leave this page. '
      + (where === 'cart' ? 'Request your quote before you leave, or call ' + phone() + '.' : 'Review estimate opens it right here to request your quote, or call ' + phone() + '.');
    return '';
  }
  function atRisk() { var d = read(); return ENABLED && mode === 'memory' && d.lines.length > 0 && !d.sentAt; }
  var leaving = false;
  var REVIEW = 'a[data-mad-finish-quote][href], a[data-mad-tray-review][href]';
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest(REVIEW) : null;
    if (!a || !atRisk()) return;
    /* A link to the page already shown (the added screen on /cart after a cart move): the form steps aside and the
       page's own estimate and request are used (a #request-quote link opens the request on hashchange); never a
       second, empty review over the page, and never a reload that would lose the estimate */
    try {
      var to = new URL(a.href, location.href);
      if (to.pathname === location.pathname) {
        if (!to.hash) {
          e.preventDefault();
          if (window.MadWizard && window.MadWizard.isOpen && window.MadWizard.isOpen()) window.MadWizard.close('done');
          var box = document.querySelector('[data-mad-estimate-list]');
          if (box && box.scrollIntoView) box.scrollIntoView({ block: 'start' });
        }
        return;
      }
    } catch (_) { /* old browsers: the review in place below */ }
    /* Codex re-check (R03): nothing keeps the estimate across pages, so the review and the quote request come to it */
    e.preventDefault();
    if (e.stopPropagation) e.stopPropagation();
    openReview(a);
  }, true);

  /* ── the review in place (R03, no storage at all) ─────────────────── */
  /* The cart page's estimate block (snippets/mad-quote-estimate-list.liquid) and its shared step (#mad-cq-modal,
     snippets/cart-quote-submit.liquid) are fetched from the cart's sections and shown in a dialog on this page, with the
     scripts that run them (mad-quote-lines.js, mad-quote-form.js, mad-quote-submit.js) and mad-quote.css, so the
     customer reviews, edits and sends the estimate this document holds. Nothing else of the cart page comes along. */
  var review = null;
  var REVIEW_SCRIPTS = [['mad-quote-lines.js', 'MadQuoteLines'], ['mad-quote-form.js', 'MadQuoteForm'], ['mad-quote-submit.js', 'MadQuoteSubmit']];
  function reviewOpen() { return !!review && review.root.getAttribute('aria-hidden') === 'false'; }
  function closeOthers() {
    try { if (window.MadWizard && window.MadWizard.isOpen && window.MadWizard.isOpen()) window.MadWizard.close('review'); } catch (e) { /* ignore */ }
    try { var tray = document.querySelector('cart-drawer'); if (tray && tray.isOpen && tray.isOpen() && tray.close) tray.close(); } catch (e) { /* ignore */ }
  }
  function buildReview() {
    var root = document.createElement('div');
    root.id = 'mad-review'; root.className = 'mad-dialog mad-review';
    root.setAttribute('role', 'dialog'); root.setAttribute('aria-modal', 'true'); root.setAttribute('aria-labelledby', 'mad-review-title'); root.setAttribute('aria-hidden', 'true');
    root.innerHTML = '<button type="button" class="mad-dialog__overlay" data-mad-review-close aria-label="Close" tabindex="-1"></button>'
      + '<div class="mad-dialog__panel"><div class="mad-dialog__header"><div class="mad-dialog__titles"><h2 id="mad-review-title" class="mad-dialog__title" tabindex="-1">Your estimate</h2></div>'
      + '<button type="button" class="mad-dialog__close" data-mad-review-close aria-label="Close">&times;</button></div>'
      + '<div class="mad-dialog__body" data-mad-review-body><p class="mad-review__status" data-mad-review-status role="status">Opening your estimate</p></div></div>';
    document.body.appendChild(root);
    return { root: root, body: root.querySelector('[data-mad-review-body]'), loaded: false, loading: false, suspended: false, returnTo: null };
  }
  function reviewStatus(text) { var el = review && review.root.querySelector('[data-mad-review-status]'); if (el) el.textContent = text; }
  function focusReview() { var t = review.root.querySelector('#mad-review-title'); if (t && t.focus) { try { t.focus(); } catch (e) { /* ignore */ } } }
  function showReview() { review.root.setAttribute('aria-hidden', 'false'); document.documentElement.classList.add('mad-modal-open'); focusReview(); }
  function hideReview(suspend) {
    if (!reviewOpen()) return;
    review.root.setAttribute('aria-hidden', 'true');
    if (!suspend) {
      document.documentElement.classList.remove('mad-modal-open');
      if (review.returnTo && document.contains(review.returnTo) && review.returnTo.focus) { try { review.returnTo.focus(); } catch (e) { /* ignore */ } }
    }
  }
  function loadScript(src, globalName) {
    return new Promise(function (resolve, reject) {
      if (window[globalName]) return resolve();
      if (!src) return reject(new Error('no ' + globalName));
      var s = document.createElement('script');
      s.src = src; s.onload = function () { resolve(); }; s.onerror = function () { reject(new Error(src)); };
      document.head.appendChild(s);
    });
  }
  /* inline scripts inside the shared step (the "How did you find us?" Other box) run when put back as new elements */
  function revive(node) {
    Array.prototype.forEach.call(node.querySelectorAll('script:not([src]):not([type="application/json"])'), function (old) {
      var s = document.createElement('script'); s.textContent = old.textContent; old.parentNode.replaceChild(s, old);
    });
  }
  function loadReview(a) {
    review.loading = true;
    var base = String(a.getAttribute('href') || '/cart').split('#')[0].split('?')[0] || '/cart';
    fetch(base + '?sections=main-cart-items,main-cart-footer', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('sections ' + r.status);
      return r.json();
    }).then(function (data) {
      var P = new DOMParser();
      var docs = [P.parseFromString(String(data['main-cart-items'] || ''), 'text/html'), P.parseFromString(String(data['main-cart-footer'] || ''), 'text/html')];
      var block = docs[0].querySelector('[data-mad-estimate-list]'), modal = docs[1].querySelector('#mad-cq-modal'), cfg = docs[1].querySelector('#mad-cq-config');
      if (!block || !modal) throw new Error('the estimate or the quote request is missing');
      var srcs = {};
      docs.forEach(function (d) {
        Array.prototype.forEach.call(d.querySelectorAll('link[rel="stylesheet"][href*="mad-quote.css"]'), function (l) {
          var href = l.getAttribute('href');
          if (!document.querySelector('link[rel="stylesheet"][href="' + href + '"]')) { var n = document.createElement('link'); n.rel = 'stylesheet'; n.href = href; document.head.appendChild(n); }
        });
        Array.prototype.forEach.call(d.querySelectorAll('script[src]'), function (s) {
          REVIEW_SCRIPTS.forEach(function (x) { if (s.getAttribute('src').indexOf(x[0]) !== -1) srcs[x[0]] = s.getAttribute('src'); });
        });
      });
      if (cfg && !document.getElementById('mad-cq-config')) document.body.appendChild(document.importNode(cfg, true));
      if (!document.getElementById('mad-cq-modal')) { var m = document.importNode(modal, true); document.body.appendChild(m); revive(m); }
      review.body.innerHTML = '';
      review.body.appendChild(document.importNode(block, true));
      var empty = document.createElement('p'); empty.className = 'mad-review__status'; empty.setAttribute('data-mad-review-empty', ''); empty.hidden = true;
      empty.textContent = 'Your estimate is empty. Close this to choose a door.';
      review.body.appendChild(empty);
      return REVIEW_SCRIPTS.reduce(function (p, x) { return p.then(function () { return loadScript(srcs[x[0]], x[1]); }); }, Promise.resolve());
    }).then(function () {
      review.loaded = true; review.loading = false;
      if (window.MadQuoteLines && window.MadQuoteLines.refresh) window.MadQuoteLines.refresh();
      document.dispatchEvent(new CustomEvent('mad:review:opened', { detail: { lines: count() } }));
    }).catch(function () {
      review.loading = false;
      reviewStatus('We could not open your estimate here. Your doors stay on this page until you leave it. Call ' + phone() + ' and we will price them with you.');
    });
  }
  function openReview(a) {
    closeOthers();
    if (!review) review = buildReview();
    review.returnTo = document.activeElement;
    review.suspended = false;
    showReview();
    if (!review.loaded && !review.loading) loadReview(a || { getAttribute: function () { return (window.MadQuoteConfig && window.MadQuoteConfig.cartUrl) || '/cart'; } });
  }
  document.addEventListener('click', function (e) {
    if (!review || !e.target || !e.target.closest || !e.target.closest('[data-mad-review-close]')) return;
    e.preventDefault(); hideReview(false);
  });
  document.addEventListener('keydown', function (e) {
    if (!reviewOpen()) return;
    if (window.MadQuoteForm && window.MadQuoteForm.isOpen && window.MadQuoteForm.isOpen()) return; /* the shared step handles its own keys */
    if (e.key === 'Escape') { e.preventDefault(); hideReview(false); return; }
    if (e.key !== 'Tab') return;
    var items = Array.prototype.filter.call(review.root.querySelectorAll('.mad-dialog__panel a[href], .mad-dialog__panel button, .mad-dialog__panel input, .mad-dialog__panel select, .mad-dialog__panel textarea, .mad-dialog__panel [tabindex]:not([tabindex="-1"])'), function (el) {
      return !el.disabled && !el.closest('[hidden]') && el.offsetParent !== null;
    });
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || !review.root.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  /* Edit on a row opens the wizard over this page: the review steps aside and comes back when the wizard closes */
  document.addEventListener('mad:wizard:opened', function () { if (reviewOpen()) { review.suspended = true; hideReview(true); } });
  document.addEventListener('mad:wizard:closed', function () { if (review && review.suspended) { review.suspended = false; showReview(); } });
  /* the shared step closing clears the page's scroll lock; the review under it keeps it */
  ['click', 'keyup'].forEach(function (t) {
    document.addEventListener(t, function () { if (reviewOpen()) setTimeout(function () { if (reviewOpen()) document.documentElement.classList.add('mad-modal-open'); }, 0); });
  });
  document.addEventListener('mad:estimate:changed', function () {
    var empty = review && review.root.querySelector('[data-mad-review-empty]');
    if (empty) empty.hidden = count() > 0;
  });
  window.addEventListener('beforeunload', function (e) {
    if (leaving || !atRisk()) return;
    e.preventDefault();
    e.returnValue = '';
  });

  window.addEventListener('storage', function (e) { if (e.key === KEY) emit('other-tab'); });
  /* Tell every consumer once the page is parsed, whatever order the deferred scripts ran in. */
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { emit('load'); });
  else setTimeout(function () { emit('load'); }, 0);

  window.MadEstimateList = {
    enabled: ENABLED, KEY: KEY, VERSION: VERSION, ADAPTER: ADAPTER, RETENTION_DAYS: RETENTION_DAYS,
    persistent: function () { return persistent; },
    storageMode: function () { return mode; }, limitText: limitText, openReview: openReview, reviewOpen: reviewOpen,
    active: function () { return ENABLED && count() > 0; },
    lines: lines, get: get, findByVariant: findByVariant, count: count, meta: meta, add: add, addAll: addAll, update: update, remove: remove, clear: clear, markSent: markSent,
    toCart: toCart, refresh: refresh
  };
})();
